import React, { useState, useEffect, useRef, useCallback } from "react";
import { api } from "../services/api";

interface SelectSearchFilterProps {
  placeholder?: string;
  value: string;
  onChange: (value: string) => void;
}

interface Customer {
  id: string;
  name: string;
}

export function SelectSearchFilter({
  placeholder = "Digite para buscar...",
  value,
  onChange,
}: SelectSearchFilterProps) {
  const [searchTerm, setSearchTerm] = useState("");
  const [customers, setCustomers] = useState<Customer[]>([]);
  const [selectedCustomer, setSelectedCustomer] = useState<Customer | null>(
    null,
  );
  const [showDropdown, setShowDropdown] = useState(false);
  const [loading, setLoading] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);

  const searchCustomers = useCallback(
    async (term = "", openDropdown = true) => {
      setLoading(true);
      try {
        const response = await api.get(`/costumers/search?name=${term}`);
        setCustomers(response.data);
        if (openDropdown) {
          setShowDropdown(true);
        }
      } catch (err) {
        setCustomers([]);
      } finally {
        setLoading(false);
      }
    },
    [],
  );

  // Load all customers on component mount (without opening dropdown)
  useEffect(() => {
    searchCustomers("", false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Debounce search - only when user is typing (dropdown already open)
  useEffect(() => {
    // Skip if searchTerm hasn't changed from user input
    if (!showDropdown) return undefined;

    const timer = setTimeout(() => {
      searchCustomers(searchTerm, false);
    }, 300);

    return () => clearTimeout(timer);
  }, [searchTerm, searchCustomers, showDropdown]);

  // Handle clicks outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node) &&
        inputRef.current &&
        !inputRef.current.contains(event.target as Node)
      ) {
        setShowDropdown(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  // Sync with external value
  useEffect(() => {
    if (!value) {
      setSelectedCustomer(null);
      setSearchTerm("");
    }
  }, [value]);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const inputValue = e.target.value;
    setSearchTerm(inputValue);

    if (!inputValue) {
      setSelectedCustomer(null);
      onChange("");
    }
  };

  const handleCustomerSelect = (customer: Customer) => {
    setSelectedCustomer(customer);
    setSearchTerm(customer.name);
    setShowDropdown(false);
    onChange(customer.id);
  };

  const handleClear = () => {
    setSelectedCustomer(null);
    setSearchTerm("");
    onChange("");
  };

  return (
    <div className="relative w-full">
      <input
        ref={inputRef}
        type="text"
        placeholder={placeholder}
        value={searchTerm}
        onChange={handleInputChange}
        onFocus={() => {
          setShowDropdown(true);
          if (customers.length === 0 && !loading) {
            searchCustomers(searchTerm, false);
          }
        }}
        className="h-[52px] px-4 w-full rounded-lg text-[#7FA5B2] bg-white border border-gray-200 focus:border-[#0085BD] focus:ring-1 focus:ring-[#0085BD]"
        autoComplete="off"
      />

      {loading && (
        <div className="absolute right-3 top-1/2 transform -translate-y-1/2">
          <div className="animate-spin h-4 w-4 border-2 border-[#0085BD] border-t-transparent rounded-full" />
        </div>
      )}

      {!loading && selectedCustomer && (
        <button
          type="button"
          onClick={handleClear}
          className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
          title="Limpar"
        >
          ✕
        </button>
      )}

      {showDropdown && customers.length > 0 && (
        <div
          ref={dropdownRef}
          className="absolute z-[1000] w-full mt-1 bg-white border border-gray-200 rounded-lg shadow-lg max-h-60 overflow-y-auto"
        >
          <button
            type="button"
            onClick={() => {
              handleClear();
              setShowDropdown(false);
            }}
            className="w-full text-left px-4 py-3 hover:bg-gray-50 cursor-pointer text-gray-400 border-b border-gray-100 italic"
          >
            Todos os clientes
          </button>
          {customers.map(customer => (
            <button
              type="button"
              key={customer.id}
              onClick={() => handleCustomerSelect(customer)}
              className="w-full text-left px-4 py-3 hover:bg-gray-50 cursor-pointer text-[#7FA5B2] border-b border-gray-100 last:border-b-0"
            >
              {customer.name}
            </button>
          ))}
        </div>
      )}

      {showDropdown &&
        searchTerm.length > 0 &&
        customers.length === 0 &&
        !loading && (
          <div
            ref={dropdownRef}
            className="absolute z-[1000] w-full mt-1 bg-white border border-gray-200 rounded-lg shadow-lg"
          >
            <div className="px-4 py-3 text-gray-500 text-center">
              Nenhum cliente encontrado
            </div>
          </div>
        )}
    </div>
  );
}
